import React, { useMemo, useState } from 'react';
import { Tree, Tag, Tooltip } from 'antd';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RechartsTooltip } from 'recharts';

/**
 * LocationTreeWithChart exibe:
 *  - À esquerda (50%): o PieChart de tasks por location
 *  - À direita (50%): a árvore de locais compacta
 */
function LocationTreeWithChart({ tasksByLocation, handleAnswersSearchLocation }) {
    // Estados de seleção e hover para o gráfico
    const [activeSlices, setActiveSlices] = useState([]);
    const [hoveredSlice, setHoveredSlice] = useState(null);
    const [hasAnimated, setHasAnimated] = useState(false);

    // Mapeia caminho -> ID original
    const idMap = useMemo(
        () => tasksByLocation.reduce((map, { locationName, locationId }) => {
            map[locationName] = locationId;
            return map;
        }, {}),
        [tasksByLocation]
    );

    // Soma recursiva: próprio + filhos
    const aggregatedCounts = useMemo(() => {
        const map = {};
        tasksByLocation.forEach(({ locationName, totalTasks }) => {
            let prefix = '';
            locationName.split(' > ').forEach(part => {
                prefix = prefix ? `${prefix} > ${part}` : part;
                map[prefix] = (map[prefix] || 0) + totalTasks;
            });
        });
        return map;
    }, [tasksByLocation]);

    // Tarefas apenas no nível exato
    const ownCounts = useMemo(
        () => tasksByLocation.reduce((map, { locationName, totalTasks }) => {
            map[locationName] = (map[locationName] || 0) + totalTasks;
            return map;
        }, {}),
        [tasksByLocation]
    );

    // Monta árvore com tags, tooltips, desabilitação de nós e estilo compacto
    const treeData = useMemo(() => {
        const nodeMap = Object.entries(aggregatedCounts).reduce((acc, [fullKey, total]) => {
            const label = fullKey.split(' > ').pop();
            const own = ownCounts[fullKey] || 0;
            const hasId = idMap[fullKey] != null;
            acc[fullKey] = {
                key: fullKey,
                title: (
                    <span style={{ fontSize: 12, lineHeight: '15px' }}>
                        <Tooltip title="Tarefas somente neste nível">
                            <Tag color={own > 0 ? '#4A90E2' : undefined} style={{ marginRight: 4, padding: "4px 6px", lineHeight: "10px" }}>{own}</Tag>
                        </Tooltip>
                        <strong>{label} {total != own ? <>- (Total: {total})</> : ""}</strong>
                    </span>
                ),
                children: [],
                disabled: !hasId,
                style: { padding: '0px' }
            };
            return acc;
        }, {});

        Object.keys(nodeMap).forEach(fullKey => {
            const idx = fullKey.lastIndexOf(' > ');
            if (idx !== -1) {
                const parentKey = fullKey.slice(0, idx);
                nodeMap[parentKey].children.push(nodeMap[fullKey]);
            }
        });

        return Object.values(nodeMap).filter(node => !node.key.includes(' > '));
    }, [aggregatedCounts, ownCounts, idMap]);

    // Prepara dados do PieChart (locations com respostas diretas)
    const pieData = useMemo(
        () => tasksByLocation
            .filter(({ totalTasks }) => totalTasks > 0)
            .map(({ locationName, totalTasks, locationId }) => ({
                name: locationName.split(' > ').pop(),
                value: totalTasks,
                id: locationId,
            })),
        [tasksByLocation]
    );

    // Paleta de cores simples
    const COLORS = ['#4A90E2', '#1B3A7C', '#F5A623', '#D622A9', '#50E3C2', '#7ED321', '#F8E71C', '#F45B69', '#417505', '#FF7F50'];

    // Alterna seleção
    const toggleSelection = (id) => {
        setActiveSlices(prev => {
            const isActive = prev.includes(id);
            const newArr = isActive ? prev.filter(i => i !== id) : [...prev, id];
            handleAnswersSearchLocation(id, 'combo', true);
            return newArr;
        });
    };

    // Handlers de clique/hover no Pie
    const onPieClick = (_, index) => pieData[index] && toggleSelection(pieData[index].id);
    const onPieMouseEnter = (_, index) => setHoveredSlice(pieData[index]?.id || null);
    const onPieMouseLeave = () => setHoveredSlice(null);

    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            {/* Gráfico de pizza */}
            <div style={{ width: '50%', height: tasksByLocation && tasksByLocation.length > 0 ? 300 : 0, maxHeight: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={40}
                            outerRadius={70}
                            minAngle={10}
                            isAnimationActive={!hasAnimated}
                            animationBegin={0}
                            animationDuration={400}
                            animationEasing="ease-out"
                            onAnimationEnd={() => setHasAnimated(true)}
                            onClick={onPieClick}
                            onMouseEnter={onPieMouseEnter}
                            onMouseLeave={onPieMouseLeave}
                            cursor="pointer"
                            label={({ name, value, percent, cx, cy, midAngle, outerRadius }) => {
                                const RADIAN = Math.PI / 180;
                                const radius = outerRadius + 25;
                                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                return (
                                    <text
                                        x={x}
                                        y={y}
                                        fill="black"
                                        textAnchor={x > cx ? "start" : "end"}
                                        dominantBaseline="central"
                                        fontSize="12px"
                                        style={{
                                            background: "rgba(255, 255, 255, 0.8)",
                                            padding: "2px",
                                            borderRadius: "3px"
                                        }}
                                    >
                                        <tspan fontWeight="bold">{`${name}: ${value} (${percent.toFixed(2)}%)`}</tspan>
                                    </text>
                                );
                            }}
                        >
                            {pieData.map((entry, idx) => {
                                const opacity = hoveredSlice != null
                                    ? (entry.id === hoveredSlice ? 1 : 0.4)
                                    : (activeSlices.length === 0 || activeSlices.includes(entry.id) ? 1 : 0.4);
                                const style = hoveredSlice === entry.id
                                    ? { stroke: '#fff', strokeWidth: 2 }
                                    : {};
                                return (
                                    <Cell
                                        key={entry.id}
                                        fill={COLORS[idx % COLORS.length]}
                                        opacity={opacity}
                                        style={style}
                                    />
                                );
                            })}
                        </Pie>
                        <RechartsTooltip formatter={value => [`${value}`, 'Tasks']} />
                    </PieChart>
                </ResponsiveContainer>
            </div>

            {/* Árvore compacta */}
            <div className="ant-loc" style={{ width: '50%', height: "100%", maxHeight: 300, overflowY: 'auto' }}>
                <Tree
                    treeData={treeData}
                    indent={8}
                    virtual                  // habilita virtualização
                    height={300}             // altura total do container
                    itemHeight={20}          // altura de cada nó
                    defaultExpandAll
                    showIcon={false}         // esconde ícones padrões
                    showLine={false}         // esconde linhas de conexão
                    onSelect={([key]) => {
                        const id = idMap[key];
                        if (id != null) handleAnswersSearchLocation(id, 'combo', true);
                    }}
                />
            </div>
        </div >
    );
}

export default React.memo(LocationTreeWithChart);
